/**
 * Dev/test fixture data for the sourcing auction feature.
 *
 * Used as the data source for MSW handlers in integration tests.
 */
import {
  AuctionVehicleDetailResponseBody,
  AuctionVehicleResponseBody,
  GetAuctionVehicleListApiResponse,
} from '../types';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const FORTY_FIVE_MINUTES_MS = 45 * MINUTE_MS;

const FEATURE_SCORE_MULTIPLIER = 100;

type AuctionFixture = Omit<AuctionVehicleResponseBody, 'carEndTime'> & {endOffsetMs: number};

const AUCTION_FIXTURES: ReadonlyArray<AuctionFixture> = [
  {
    pk: '2538001',
    auctionId: '29001',
    vin: 'WBA5A5C50ED512345',
    make: 'BMW',
    model: '5 Series',
    adTitle: '5 SERIES / 2021 / 4D / 530d xDrive',
    mileage: 45000,
    year: 2021,
    registrationDate: '2021-03-15',
    fuelType: 'Diesel',
    transmission: 'Automatic',
    power: 210,
    cubicCapacity: 2993,
    sellerCountry: 'CZE',
    priceActual: 620000,
    priceStart: 550000,
    priceBuyNow: 750000,
    priceMinimum: null,
    priceReserve: null,
    currency: 'CZK',
    picturesUrl: ['https://car-data.b-cdn.net/2538001/placeholder_1.jpg'],
    url: 'https://auction-portal.example.com/lot/29001',
    auctionName: 'ALD Automotive',
    description: '',
    marketPosition: 1,
    marketPositionCount: 20,
    predictedPrice: 700000,
    maximalPossibleMargin: 80000,
    driveType: '4x4',
    carStyle: 'Sedan',
    featureScore: 0.7 * FEATURE_SCORE_MULTIPLIER,
    vatRate: 0.21,
    adId: null,
    server: null,
    originalCurrency: null,
    modelFamilyGroup: null,
    doorCount: null,
    interiorMaterial: null,
    vatReclaimable: null,
    color: null,
    colorSpecification: null,
    highlightFeatures: null,
    features: null,
    trimLevel: null,
    seller: null,
    technicalInspectionValidTo: null,
    originCountry: null,
    numberOfSeats: null,
    thumbnailUrl: null,
    location: null,
    attractivenessScore: null,
    marketSituationCategory: null,
    priceDynamicsCategory: null,
    buyerCountry: null,
    turnover: null,
    isHitlist: null,
    isBlacklist: null,
    suspicious: null,
    suspiciousReason: null,
    condition: null,
    manufactureDate: null,
    mileageRangeCategory: null,
    auctionType: null,
    auctionStartTime: null,
    auctionEndTime: null,
    priceActualCzk: null,
    priceActualEur: null,
    priceStartCzk: null,
    priceStartEur: null,
    priceBuyNowCzk: null,
    priceBuyNowEur: null,
    priceMinimumCzk: null,
    priceMinimumEur: null,
    priceReserveCzk: null,
    priceReserveEur: null,
    originalPriceActual: null,
    originalPriceStart: null,
    originalPriceBuyNow: null,
    originalPriceMinimum: null,
    originalPriceReserve: null,
    endOffsetMs: 2 * HOUR_MS,
  },
  {
    pk: '2538002',
    auctionId: '29002',
    vin: 'WAUZZZ4G7LN012345',
    make: 'Audi',
    model: 'A6',
    adTitle: 'A6 / 2020 / 4D / 3.0 TDI quattro',
    mileage: 62000,
    year: 2020,
    registrationDate: '2020-06-01',
    fuelType: 'Diesel',
    transmission: 'Automatic',
    power: 180,
    cubicCapacity: 2967,
    sellerCountry: 'CZE',
    priceActual: 450000,
    priceStart: 380000,
    priceBuyNow: null,
    priceMinimum: null,
    priceReserve: null,
    currency: 'CZK',
    picturesUrl: ['https://car-data.b-cdn.net/2538002/placeholder_1.jpg'],
    url: null,
    auctionName: 'LeasePlan',
    description: 'This garage charges an administrative fee of 1 000 Kč excluding VAT.',
    marketPosition: 3,
    marketPositionCount: 15,
    predictedPrice: 500000,
    maximalPossibleMargin: 50000,
    driveType: '4x4',
    carStyle: 'Sedan',
    featureScore: 0.5 * FEATURE_SCORE_MULTIPLIER,
    vatRate: 0.21,
    adId: null,
    server: null,
    originalCurrency: null,
    modelFamilyGroup: null,
    doorCount: null,
    interiorMaterial: null,
    vatReclaimable: null,
    color: null,
    colorSpecification: null,
    highlightFeatures: null,
    features: null,
    trimLevel: null,
    seller: null,
    technicalInspectionValidTo: null,
    originCountry: null,
    numberOfSeats: null,
    thumbnailUrl: null,
    location: null,
    attractivenessScore: null,
    marketSituationCategory: null,
    priceDynamicsCategory: null,
    buyerCountry: null,
    turnover: null,
    isHitlist: null,
    isBlacklist: null,
    suspicious: null,
    suspiciousReason: null,
    condition: null,
    manufactureDate: null,
    mileageRangeCategory: null,
    auctionType: null,
    auctionStartTime: null,
    auctionEndTime: null,
    priceActualCzk: null,
    priceActualEur: null,
    priceStartCzk: null,
    priceStartEur: null,
    priceBuyNowCzk: null,
    priceBuyNowEur: null,
    priceMinimumCzk: null,
    priceMinimumEur: null,
    priceReserveCzk: null,
    priceReserveEur: null,
    originalPriceActual: null,
    originalPriceStart: null,
    originalPriceBuyNow: null,
    originalPriceMinimum: null,
    originalPriceReserve: null,
    endOffsetMs: 26 * HOUR_MS,
  },
  {
    pk: '2538003',
    auctionId: '29003',
    vin: 'WDD2130041A012345',
    make: 'Mercedes-Benz',
    model: 'E-Class',
    adTitle: 'E-CLASS / 2022 / 4D / E 220 d',
    mileage: 18000,
    year: 2022,
    registrationDate: '2022-01-10',
    fuelType: 'Diesel',
    transmission: 'Automatic',
    power: 147,
    cubicCapacity: 1993,
    sellerCountry: 'SVK',
    priceActual: 750000,
    priceStart: 750000,
    priceBuyNow: 1100000,
    priceMinimum: null,
    priceReserve: 900000,
    currency: 'CZK',
    picturesUrl: ['https://car-data.b-cdn.net/2538003/placeholder_1.jpg'],
    url: 'https://auction-portal.example.com/lot/29003',
    auctionName: 'Alphabet',
    description: '',
    marketPosition: 2,
    marketPositionCount: 25,
    predictedPrice: 900000,
    maximalPossibleMargin: 150000,
    driveType: 'RWD',
    carStyle: 'Sedan',
    featureScore: 0.8 * FEATURE_SCORE_MULTIPLIER,
    vatRate: 0.21,
    adId: null,
    server: null,
    originalCurrency: null,
    modelFamilyGroup: null,
    doorCount: null,
    interiorMaterial: null,
    vatReclaimable: null,
    color: null,
    colorSpecification: null,
    highlightFeatures: null,
    features: null,
    trimLevel: null,
    seller: null,
    technicalInspectionValidTo: null,
    originCountry: null,
    numberOfSeats: null,
    thumbnailUrl: null,
    location: null,
    attractivenessScore: null,
    marketSituationCategory: null,
    priceDynamicsCategory: null,
    buyerCountry: null,
    turnover: null,
    isHitlist: null,
    isBlacklist: null,
    suspicious: null,
    suspiciousReason: null,
    condition: null,
    manufactureDate: null,
    mileageRangeCategory: null,
    auctionType: null,
    auctionStartTime: null,
    auctionEndTime: null,
    priceActualCzk: null,
    priceActualEur: null,
    priceStartCzk: null,
    priceStartEur: null,
    priceBuyNowCzk: null,
    priceBuyNowEur: null,
    priceMinimumCzk: null,
    priceMinimumEur: null,
    priceReserveCzk: null,
    priceReserveEur: null,
    originalPriceActual: null,
    originalPriceStart: null,
    originalPriceBuyNow: null,
    originalPriceMinimum: null,
    originalPriceReserve: null,
    endOffsetMs: 3 * DAY_MS,
  },
  {
    pk: '2538004',
    auctionId: '29004',
    vin: 'WVWZZZ3CZKE012345',
    make: 'Volkswagen',
    model: 'Passat',
    adTitle: 'PASSAT / 2019 / 4D / 2.0 TDI',
    mileage: 87000,
    year: 2019,
    registrationDate: '2019-08-20',
    fuelType: 'Diesel',
    transmission: 'Manual',
    power: 110,
    cubicCapacity: 1968,
    sellerCountry: 'AUT',
    priceActual: 280000,
    priceStart: 200000,
    priceBuyNow: null,
    priceMinimum: 250000,
    priceReserve: 250000,
    currency: 'CZK',
    picturesUrl: ['https://car-data.b-cdn.net/2538004/placeholder_1.jpg'],
    url: null,
    auctionName: 'VWFS Auction',
    description: 'Vehicle available for inspection by appointment only.',
    marketPosition: 8,
    marketPositionCount: 18,
    predictedPrice: 250000,
    maximalPossibleMargin: -30000,
    driveType: 'FWD',
    carStyle: 'Sedan',
    featureScore: 0.3 * FEATURE_SCORE_MULTIPLIER,
    vatRate: 0.21,
    adId: null,
    server: null,
    originalCurrency: null,
    modelFamilyGroup: null,
    doorCount: null,
    interiorMaterial: null,
    vatReclaimable: null,
    color: null,
    colorSpecification: null,
    highlightFeatures: null,
    features: null,
    trimLevel: null,
    seller: null,
    technicalInspectionValidTo: null,
    originCountry: null,
    numberOfSeats: null,
    thumbnailUrl: null,
    location: null,
    attractivenessScore: null,
    marketSituationCategory: null,
    priceDynamicsCategory: null,
    buyerCountry: null,
    turnover: null,
    isHitlist: null,
    isBlacklist: null,
    suspicious: null,
    suspiciousReason: null,
    condition: null,
    manufactureDate: null,
    mileageRangeCategory: null,
    auctionType: null,
    auctionStartTime: null,
    auctionEndTime: null,
    priceActualCzk: null,
    priceActualEur: null,
    priceStartCzk: null,
    priceStartEur: null,
    priceBuyNowCzk: null,
    priceBuyNowEur: null,
    priceMinimumCzk: null,
    priceMinimumEur: null,
    priceReserveCzk: null,
    priceReserveEur: null,
    originalPriceActual: null,
    originalPriceStart: null,
    originalPriceBuyNow: null,
    originalPriceMinimum: null,
    originalPriceReserve: null,
    endOffsetMs: -HOUR_MS,
  },
  {
    pk: '2538005',
    auctionId: '29005',
    vin: 'TMBZZZ1Z7P012345',
    make: 'Škoda',
    model: 'Octavia',
    adTitle: 'OCTAVIA / 2023 / 4D / 2.0 TDI Style',
    mileage: 12000,
    year: 2023,
    registrationDate: '2023-02-14',
    fuelType: 'Diesel',
    transmission: 'Automatic',
    power: 110,
    cubicCapacity: 1968,
    sellerCountry: 'CZE',
    priceActual: 520000,
    priceStart: 480000,
    priceBuyNow: 600000,
    priceMinimum: 500000,
    priceReserve: 520000,
    currency: 'CZK',
    picturesUrl: ['https://car-data.b-cdn.net/2538005/placeholder_1.jpg'],
    url: 'https://auction-portal.example.com/lot/29005',
    auctionName: 'ALD Automotive',
    description: 'This garage charges an administrative fee of 1 000 Kč excluding VAT.',
    marketPosition: 1,
    marketPositionCount: 12,
    predictedPrice: 580000,
    maximalPossibleMargin: 60000,
    driveType: 'FWD',
    carStyle: 'Liftback',
    featureScore: 0.6 * FEATURE_SCORE_MULTIPLIER,
    vatRate: 0.21,
    adId: null,
    server: null,
    originalCurrency: null,
    modelFamilyGroup: null,
    doorCount: null,
    interiorMaterial: null,
    vatReclaimable: null,
    color: null,
    colorSpecification: null,
    highlightFeatures: null,
    features: null,
    trimLevel: null,
    seller: null,
    technicalInspectionValidTo: null,
    originCountry: null,
    numberOfSeats: null,
    thumbnailUrl: null,
    location: null,
    attractivenessScore: null,
    marketSituationCategory: null,
    priceDynamicsCategory: null,
    buyerCountry: null,
    turnover: null,
    isHitlist: null,
    isBlacklist: null,
    suspicious: null,
    suspiciousReason: null,
    condition: null,
    manufactureDate: null,
    mileageRangeCategory: null,
    auctionType: null,
    auctionStartTime: null,
    auctionEndTime: null,
    priceActualCzk: null,
    priceActualEur: null,
    priceStartCzk: null,
    priceStartEur: null,
    priceBuyNowCzk: null,
    priceBuyNowEur: null,
    priceMinimumCzk: null,
    priceMinimumEur: null,
    priceReserveCzk: null,
    priceReserveEur: null,
    originalPriceActual: null,
    originalPriceStart: null,
    originalPriceBuyNow: null,
    originalPriceMinimum: null,
    originalPriceReserve: null,
    endOffsetMs: FORTY_FIVE_MINUTES_MS,
  },
  {
    pk: '2538006',
    auctionId: '29006',
    vin: 'JTMBFREV6GJ012345',
    make: 'Toyota',
    model: 'RAV4',
    adTitle: 'RAV4 / 2022 / 5D / 2.5 Hybrid AWD',
    mileage: 34000,
    year: 2022,
    registrationDate: '2022-05-01',
    fuelType: 'Hybrid',
    transmission: 'CVT',
    power: 131,
    cubicCapacity: 2487,
    sellerCountry: 'DEU',
    priceActual: 850000,
    priceStart: 850000,
    priceBuyNow: null,
    priceMinimum: null,
    priceReserve: 950000,
    currency: 'CZK',
    picturesUrl: ['https://car-data.b-cdn.net/2538006/placeholder_1.jpg'],
    url: null,
    auctionName: 'Arval',
    description: '',
    marketPosition: 5,
    marketPositionCount: 20,
    predictedPrice: 950000,
    maximalPossibleMargin: 100000,
    driveType: '4x4',
    carStyle: 'SUV',
    featureScore: 0.55 * FEATURE_SCORE_MULTIPLIER,
    vatRate: 0.21,
    adId: null,
    server: null,
    originalCurrency: null,
    modelFamilyGroup: null,
    doorCount: null,
    interiorMaterial: null,
    vatReclaimable: null,
    color: null,
    colorSpecification: null,
    highlightFeatures: null,
    features: null,
    trimLevel: null,
    seller: null,
    technicalInspectionValidTo: null,
    originCountry: null,
    numberOfSeats: null,
    thumbnailUrl: null,
    location: null,
    attractivenessScore: null,
    marketSituationCategory: null,
    priceDynamicsCategory: null,
    buyerCountry: null,
    turnover: null,
    isHitlist: null,
    isBlacklist: null,
    suspicious: null,
    suspiciousReason: null,
    condition: null,
    manufactureDate: null,
    mileageRangeCategory: null,
    auctionType: null,
    auctionStartTime: null,
    auctionEndTime: null,
    priceActualCzk: null,
    priceActualEur: null,
    priceStartCzk: null,
    priceStartEur: null,
    priceBuyNowCzk: null,
    priceBuyNowEur: null,
    priceMinimumCzk: null,
    priceMinimumEur: null,
    priceReserveCzk: null,
    priceReserveEur: null,
    originalPriceActual: null,
    originalPriceStart: null,
    originalPriceBuyNow: null,
    originalPriceMinimum: null,
    originalPriceReserve: null,
    endOffsetMs: 5 * DAY_MS,
  },
];

function buildAuctions(): AuctionVehicleResponseBody[] {
  const now = Date.now();
  return AUCTION_FIXTURES.map(({endOffsetMs, ...rest}) => ({
    ...rest,
    carEndTime: new Date(now + endOffsetMs).toISOString(), // eslint-disable-line no-restricted-syntax -- constructing a Date from a number (not parsing a string)
  }));
}

export function buildMockAuctionListResponse(): GetAuctionVehicleListApiResponse {
  const data = buildAuctions();
  return {
    data: data.map((auctionVehicle) => ({
      favourite: false,
      comparison: false,
      seen: false,
      inCart: false,
      hidden: false,
      vehicleSummary: null,
      auctionVehicle,
      features: {},
      highlights: [],
      shortcomings: [],
    })),
    links: {size: 20, offset: 0},
  };
}

export function buildMockAuctionDetail(
  auctionId: string
): AuctionVehicleDetailResponseBody | undefined {
  const auctionVehicle = buildAuctions().find((a) => a.auctionId === auctionId);
  if (!auctionVehicle) {
    return undefined;
  }
  return {
    favourite: false,
    comparison: false,
    seen: false,
    inCart: false,
    hidden: false,
    vehicleSummary: null,
    auctionVehicle,
    features: {},
    highlights: [],
    shortcomings: [],
  };
}
